import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '@/lib/api/services'
import { refreshAuth } from '@/lib/api/authApi'
import { registerSessionClearHandler, registerTokenRefreshHandler } from '@/lib/auth/sessionBridge'
import { getErrorMessage } from '@/lib/utils/errorHandler'
import { isTokenExpired } from '@/lib/utils/tokenUtils'
import { appEnv } from '@/lib/env'

export interface User {
  id: string
  email: string
  name: string
  tenantId: string
  role?: 'owner' | 'admin' | 'employee'
  canCreateJobs?: boolean
  canScheduleAppointments?: boolean
  canSeeOtherJobs?: boolean
  canSeeJobPrices?: boolean
  canEditJobs?: boolean
  canEditAssignedJobsOnly?: boolean
  onboardingCompletedAt?: string | null
}

interface PendingChallenge {
  session: string
  email: string
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  pendingChallenge: PendingChallenge | null
  login: (email: string, password: string) => Promise<void>
  completeNewPasswordChallenge: (newPassword: string) => Promise<void>
  clearPendingChallenge: () => void
  register: (data: {
    email: string
    password: string
    name: string
    companyName: string
    plan: 'single' | 'team' | 'team-plus'
  }) => Promise<{ checkoutUrl?: string } | void>
  completeSignup: (data: {
    session_id: string
    name: string
    companyName?: string
    password: string
  }) => Promise<void>
  testerSignup: (data: {
    email: string
    name: string
    companyName?: string
    password: string
    code: string
  }) => Promise<void>
  refreshAccessToken: () => Promise<boolean>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  confirmResetPassword: (token: string, newPassword: string) => Promise<void>
  clearError: () => void
  clearSession: () => void
  checkTokenValidity: () => boolean
  updateUser: (updates: Partial<Pick<User, 'name' | 'email'>>) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Allow the API client to update auth store state without importing this module,
      // avoiding circular dependencies that can break Rollup builds on some platforms.
      registerTokenRefreshHandler(({ token, refreshToken, user }) => {
        set({
          user: (user as User | undefined) ?? get().user,
          token,
          refreshToken,
          isAuthenticated: true,
          error: null,
        })
      })

      registerSessionClearHandler(() => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      })

      return {
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        pendingChallenge: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, pendingChallenge: null })
        try {
          const response = await authService.login(email, password)

          if (response.challengeRequired === 'NEW_PASSWORD_REQUIRED' && response.session && response.email) {
            set({
              pendingChallenge: { session: response.session, email: response.email },
              isLoading: false,
              error: null,
            })
            return
          }

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
          localStorage.setItem('auth_token', response.token)
          localStorage.setItem('refresh_token', response.refreshToken)
          localStorage.setItem('tenant_id', response.user.tenantId)
        } catch (error: any) {
          const friendlyMessage = getErrorMessage(error, 'Login failed. Please try again.')
          set({
            error: friendlyMessage,
            isLoading: false,
            isAuthenticated: false,
          })
          setTimeout(() => {
            set({ isLoading: false })
          }, 100)
          throw error
        }
      },

      completeNewPasswordChallenge: async (newPassword: string) => {
        const { pendingChallenge } = get()
        if (!pendingChallenge) {
          set({ error: 'Session expired. Please sign in again.' })
          return
        }
        set({ isLoading: true, error: null })
        try {
          const response = await authService.respondToNewPasswordChallenge(
            pendingChallenge.session,
            pendingChallenge.email,
            newPassword
          )
          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            pendingChallenge: null,
          })
          localStorage.setItem('auth_token', response.token)
          localStorage.setItem('refresh_token', response.refreshToken)
          localStorage.setItem('tenant_id', response.user.tenantId)
        } catch (error: any) {
          const friendlyMessage = getErrorMessage(error, 'Failed to set new password. Please try again.')
          set({
            error: friendlyMessage,
            isLoading: false,
          })
          throw error
        }
      },

      clearPendingChallenge: () => set({ pendingChallenge: null, error: null }),

      register: async data => {
        set({ isLoading: true, error: null })
        try {
          const response = await authService.register(data)
          if (response.checkoutUrl) {
            set({ isLoading: false, error: null })
            return { checkoutUrl: response.checkoutUrl }
          }
          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
          localStorage.setItem('auth_token', response.token)
          localStorage.setItem('refresh_token', response.refreshToken)
          localStorage.setItem('tenant_id', response.user.tenantId)
        } catch (error: any) {
          const friendlyMessage = getErrorMessage(error, 'Registration failed. Please try again.')
          set({
            error: friendlyMessage,
            isLoading: false,
            isAuthenticated: false,
          })
          throw error
        }
      },

      completeSignup: async (data: {
        session_id: string
        name: string
        companyName?: string
        password: string
      }) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authService.completeSignup(data)
          const user = response.user as User
          set({
            user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
          localStorage.setItem('auth_token', response.token)
          localStorage.setItem('refresh_token', response.refreshToken)
          localStorage.setItem('tenant_id', user.tenantId)
        } catch (error: any) {
          const friendlyMessage = getErrorMessage(error, 'Failed to create account. Please try again.')
          set({
            error: friendlyMessage,
            isLoading: false,
            isAuthenticated: false,
          })
          throw error
        }
      },

      testerSignup: async (data: {
        email: string
        name: string
        companyName?: string
        password: string
        code: string
      }) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authService.testerSignup(data)
          const user = response.user as User
          set({
            user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
          localStorage.setItem('auth_token', response.token)
          localStorage.setItem('refresh_token', response.refreshToken)
          localStorage.setItem('tenant_id', user.tenantId)
        } catch (error) {
          const friendlyMessage = getErrorMessage(error, 'Failed to create account. Please try again.')
          set({
            error: friendlyMessage,
            isLoading: false,
            isAuthenticated: false,
          })
          throw error
        }
      },

      refreshAccessToken: async () => {
        // In mock data mode there is no real backend to refresh against, and the
        // mock token isn't a real JWT, so a refresh attempt would fail and clear
        // the session. Treat the mock session as always valid. (Matches the same
        // guard in SessionMonitor and the apiClient interceptor.)
        if (appEnv.isMock) {
          return true
        }

        const currentRefreshToken = get().refreshToken || localStorage.getItem('refresh_token')

        if (!currentRefreshToken) {
          console.warn('No refresh token available')
          return false
        }

        try {
          const response = await refreshAuth(currentRefreshToken)
          set({
            user: (response.user as User | undefined) ?? get().user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            error: null,
          })
          // Update stored tokens
          localStorage.setItem('auth_token', response.token)
          localStorage.setItem('refresh_token', response.refreshToken)
          const maybeUser = response.user as Partial<User> | undefined
          if (maybeUser?.tenantId) {
            localStorage.setItem('tenant_id', maybeUser.tenantId)
          }
          return true
        } catch (error: any) {
          console.error('Token refresh failed:', error)
          // If refresh fails, clear session
          get().clearSession()
          return false
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await authService.logout()
        } catch (error) {
          // Continue with logout even if API call fails
          console.error('Logout error:', error)
        } finally {
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
          // Clear all auth tokens
          localStorage.removeItem('auth_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('tenant_id')
        }
      },

      resetPassword: async (email: string) => {
        set({ isLoading: true, error: null })
        try {
          await authService.resetPassword(email)
          set({ isLoading: false })
        } catch (error: any) {
          const friendlyMessage = getErrorMessage(
            error,
            'Failed to send reset email. Please try again.'
          )
          set({
            error: friendlyMessage,
            isLoading: false,
          })
          throw error
        }
      },

      confirmResetPassword: async (token: string, newPassword: string) => {
        set({ isLoading: true, error: null })
        try {
          await authService.confirmResetPassword(token, newPassword)
          set({ isLoading: false })
        } catch (error: any) {
          const friendlyMessage = getErrorMessage(
            error,
            'Failed to reset password. Please try again.'
          )
          set({
            error: friendlyMessage,
            isLoading: false,
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),

      clearSession: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('tenant_id')
      },

      checkTokenValidity: () => {
        const token = localStorage.getItem('auth_token')
        if (!token) {
          return false
        }

        // Check if token is expired (with 60 second buffer)
        if (isTokenExpired(token, 60)) {
          // Token is expired, clear session
          useAuthStore.getState().clearSession()
          return false
        }

        return true
      },

      updateUser: (updates) => {
        const current = get().user
        if (current) {
          set({ user: { ...current, ...updates } })
        }
      },
      }
    },
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
